import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { getApplicationDetail, reviewApplication } from "@/lib/owner.functions";
import { StatusPill } from "@/components/StatusPill";
import { ukDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/owner/applications/$applicationId")({
  head: () => ({
    meta: [
      { title: "Review application — Shields Control Room" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ApplicationReview,
});

type Decision = "approved" | "rejected" | "more_info_required" | "suspended";

function Field({ label, value }: { label: string; value?: string | number | null | undefined }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm">{value ?? "—"}</p>
    </div>
  );
}

function ApplicationReview() {
  const { applicationId } = useParams({ from: Route.id });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ["owner-application", applicationId],
    queryFn: () => getApplicationDetail({ data: { applicationId } }),
    retry: false,
  });

  async function decide(decision: Decision) {
    if (decision !== "approved" && notes.trim().length < 3) {
      toast.error("Add a note — it's recorded in the audit trail and shared with the applicant.");
      return;
    }
    setBusy(true);
    try {
      const result = await reviewApplication({
        data: { applicationId, decision, notes: notes.trim() || null },
      });
      if (!result.ok) {
        toast.error(result.error ?? "Decision failed");
        return;
      }
      toast.success(`Application ${decision.replaceAll("_", " ")}`);
      setNotes("");
      await queryClient.invalidateQueries({ queryKey: ["owner-application", applicationId] });
      await queryClient.invalidateQueries({ queryKey: ["owner-dashboard"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Decision failed. Please retry.");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (error || !data || !data.ok) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-muted-foreground">
          {data && !data.ok ? data.error : "Application not found, or you're not an owner."}
        </p>
        <Button variant="outline" onClick={() => navigate({ to: "/owner" })} className="min-h-11">
          Back to Control Room
        </Button>
      </div>
    );
  }

  const a = data.application;
  const decided = !["submitted", "under_review", "more_info_required"].includes(a.status);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="section-pad mx-auto flex h-16 max-w-4xl items-center justify-between">
          <Link
            to="/owner"
            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden /> Control Room
          </Link>
          <span className="font-display text-sm font-bold">Application review</span>
        </div>
      </header>

      <main className="section-pad mx-auto max-w-4xl py-10">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold sm:text-3xl">{a.trading_name}</h1>
          <StatusPill status={a.status} label={a.status.replaceAll("_", " ")} />
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Submitted {ukDateTime(a.submitted_at)}
          {a.decided_at ? ` · decided ${ukDateTime(a.decided_at)}` : ""}
        </p>

        <div className="card-surface mt-8 grid gap-5 p-6 sm:grid-cols-2">
          <Field label="Applicant" value={data.profile?.full_name} />
          <Field label="Account email" value={data.profile?.email ?? a.contact_email} />
          <Field label="Contact email" value={a.contact_email} />
          <Field label="Contact phone" value={a.contact_phone} />
          <Field label="Legal name" value={a.legal_name} />
          <Field label="Company number" value={a.company_number} />
          <Field label="Years of experience" value={a.years_experience} />
          <Field label="Operating areas" value={a.operating_areas} />
          <Field label="Insurance provider" value={a.insurance_provider} />
          <Field label="Insurance policy" value={a.insurance_policy} />
          <Field label="Insurance expires" value={a.insurance_expires} />
          <div className="sm:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Services</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(a.services ?? []).map((s: string) => (
                <span key={s} className="rounded-full bg-muted px-3 py-1 text-xs font-semibold">
                  {s}
                </span>
              ))}
            </div>
          </div>
          {a.reviewer_notes && (
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Latest reviewer notes
              </p>
              <p className="mt-1 whitespace-pre-wrap rounded-lg bg-muted p-3 text-sm">{a.reviewer_notes}</p>
            </div>
          )}
        </div>

        <section className="card-surface mt-8 p-6">
          <h2 className="font-display text-lg font-bold">Record a decision</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every decision is written to the audit trail with your notes and emailed to the applicant.
            Approving grants partner access and seeds their public profile.
          </p>
          {decided && (
            <p className="mt-3 rounded-lg bg-muted px-4 py-3 text-sm font-semibold">
              This application has been decided ({a.status.replaceAll("_", " ")}). You can still record a
              new decision below if circumstances change.
            </p>
          )}
          <div className="mt-4">
            <Label htmlFor="notes">Notes / reason</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Required for reject, more-info and suspend decisions…"
              className="mt-1.5"
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => decide("approved")} className="min-h-11 font-bold">
              Approve
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => decide("more_info_required")} className="min-h-11">
              Request more info
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => decide("rejected")} className="min-h-11">
              Reject
            </Button>
            <Button variant="destructive" disabled={busy} onClick={() => decide("suspended")} className="min-h-11">
              Suspend
            </Button>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <ScrollText className="h-5 w-5 text-primary" aria-hidden /> Decision history
          </h2>
          {data.history.length === 0 ? (
            <p className="card-surface mt-4 p-6 text-sm text-muted-foreground">No decisions recorded yet.</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.history.map((h) => {
                const beforeStatus = (h.before as { status?: string } | null)?.status;
                const afterStatus = (h.after as { status?: string } | null)?.status;
                return (
                <li key={h.id} className="card-surface p-4 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{h.action.replaceAll("_", " ")}</span>
                    {beforeStatus && afterStatus && (
                      <span className="text-xs text-muted-foreground">
                        {beforeStatus} → {afterStatus}
                      </span>
                    )}
                  </div>
                  {h.reason && <p className="mt-1 text-muted-foreground">{h.reason}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">{ukDateTime(h.created_at)}</p>
                </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
